import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  alpha,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import { getUsers, deleteUser, updateUserStatus, checkUserDeletable, getCompanies } from '../services/api';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import EditUserModal from '../components/modals/EditUserModal';
import AddUserModal from '../components/modals/AddUserModal';
import ResetPasswordAdminModal from '../components/modals/ResetPasswordAdminModal';
import { neonBlue, shakespeare, kepple, california, stormGrey, redOrange } from '../theme/colors';

const StatTile = ({ title, value, icon: Icon, accent, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      width: '100%',
      borderRadius: 2,
      border: `1px solid ${alpha(accent, 0.12)}`,
      background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, #ffffff 60%)`,
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': {
        boxShadow: `0 8px 24px ${alpha(accent, 0.16)}`,
        transform: 'translateY(-2px)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        bgcolor: accent,
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="caption" sx={{ color: stormGrey[500], fontWeight: 500 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, color: accent, mt: 0.5 }}>
          {loading ? <CircularProgress size={22} sx={{ color: accent }} /> : value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.14),
          color: accent,
        }}
      >
        <Icon />
      </Box>
    </Stack>
  </Paper>
);

const getRoleChipSx = (role) => {
  switch (role) {
    case 'super-admin':
      return { bgcolor: alpha(neonBlue[500], 0.12), color: neonBlue[600] };
    case 'cus-admin':
      return { bgcolor: alpha(shakespeare[500], 0.12), color: shakespeare[500] };
    case 'clerk':
      return { bgcolor: alpha(california[500], 0.12), color: california[500] };
    case 'rider':
      return { bgcolor: alpha(kepple[500], 0.12), color: kepple[600] };
    default:
      return { bgcolor: alpha(stormGrey[400], 0.12), color: stormGrey[600] };
  }
};

const UserAdminPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super-admin';
  const isCusAdmin = userRole === 'cus-admin';
  const currentUserCompanyId = localStorage.getItem('companyId');
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const companyId = queryParams.get('company');
    if (companyId) {
      setSelectedCompany(companyId);
    } else {
      setSelectedCompany('');
    }
  }, [location.search]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await getUsers();
      let fetchedUsers = response.data || [];

      if (isCusAdmin && !isSuperAdmin) {
        fetchedUsers = fetchedUsers.filter((user) => user.role !== 'rider');
      }

      setAllUsers(fetchedUsers);
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
      setAllUsers([]);
    } finally {
      setLoading(false);
    }
  }, [isCusAdmin, isSuperAdmin]);

  const users = useMemo(() => {
    let filtered = allUsers;

    if (isSuperAdmin && selectedCompany) {
      filtered = filtered.filter((user) => user.company_id === parseInt(selectedCompany));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.username?.toLowerCase().includes(term) ||
          user.name?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.phone?.toLowerCase().includes(term) ||
          user.role?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [allUsers, selectedCompany, isSuperAdmin, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany]);

  const userStats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => u.is_active).length,
      admins: users.filter((u) => u.role === 'cus-admin' || u.role === 'super-admin').length,
      clerks: users.filter((u) => u.role === 'clerk').length,
    }),
    [users]
  );

  const paginatedUsers = useMemo(() => {
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    return users.slice(indexOfFirstRecord, indexOfLastRecord);
  }, [users, currentPage]);

  const totalPages = Math.ceil(users.length / recordsPerPage);

  const fetchCompanies = useCallback(async () => {
    if (isSuperAdmin || isCusAdmin) {
      try {
        const response = await getCompanies();
        setCompanies(response.data);
      } catch (err) {
      }
    }
  }, [isSuperAdmin, isCusAdmin]);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, [fetchUsers, fetchCompanies]);

  const handleDeleteClick = async (e, user) => {
    e.stopPropagation();
    setUserToDelete(user);
    setOpenConfirmDialog(true);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const checkResponse = await checkUserDeletable(userToDelete.id);

      if (!checkResponse.data.canDelete) {
        setDeleteError(`Cannot delete user ${userToDelete.username}: User has order history`);
        setOpenConfirmDialog(false);
        return;
      }

      await deleteUser(userToDelete.id);
      setAllUsers(allUsers.filter((user) => user.id !== userToDelete.id));
      setOpenConfirmDialog(false);
      setUserToDelete(null);
      setDeleteError('');
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleStatusChange = async (e, userId, newStatus) => {
    e.stopPropagation();
    try {
      await updateUserStatus(userId, newStatus);
      setAllUsers(
        allUsers.map((user) => (user.id === userId ? { ...user, is_active: newStatus } : user))
      );
    } catch (err) {
      setAllUsers(allUsers.map((user) => (user.id === userId ? { ...user, is_active: !newStatus } : user)));
    }
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleUserCreated = (newUser) => {
    setAllUsers([newUser, ...allUsers]);
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleOpenResetPasswordModal = (e, user) => {
    e.stopPropagation();
    setUserToResetPassword(user);
    setIsResetPasswordModalOpen(true);
  };

  const handleCloseResetPasswordModal = () => {
    setIsResetPasswordModalOpen(false);
    setUserToResetPassword(null);
  };

  const handlePasswordResetSuccess = () => {
    setAlertMessage(`Password for ${userToResetPassword?.username} reset successfully.`);
    setTimeout(() => setAlertMessage(''), 3000);
  };

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompany(companyId);
    setCurrentPage(1);

    const queryParams = new URLSearchParams(location.search);
    if (companyId) {
      queryParams.set('company', companyId);
    } else {
      queryParams.delete('company');
    }

    const newSearch = queryParams.toString();
    navigate(`/admin/users${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ color: neonBlue[500] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${neonBlue[600]} 0%, ${neonBlue[500]} 50%, ${shakespeare[500]} 100%)`,
          color: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              User Management
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: alpha('#fff', 0.85) }}>
              Manage accounts, roles, and access permissions
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            {isSuperAdmin && (
              <FormControl
                size="small"
                sx={{
                  minWidth: 220,
                  bgcolor: alpha('#fff', 0.95),
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                }}
              >
                <InputLabel>Filter by Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Filter by Company"
                >
                  <MenuItem value="">
                    <em>All Companies</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddModalOpen(true)}
              sx={{
                bgcolor: '#fff',
                color: neonBlue[600],
                fontWeight: 600,
                '&:hover': { bgcolor: alpha('#fff', 0.9) },
              }}
            >
              Create User
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.5} sx={{ mb: 3, width: '100%' }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Total Users"
            value={userStats.total}
            icon={GroupsIcon}
            accent={neonBlue[500]}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Active Users"
            value={userStats.active}
            icon={CheckCircleOutlineIcon}
            accent={kepple[500]}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Admins"
            value={userStats.admins}
            icon={AdminPanelSettingsIcon}
            accent={shakespeare[500]}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            title="Clerks"
            value={userStats.clerks}
            icon={BadgeIcon}
            accent={california[500]}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${stormGrey[200]}`,
          bgcolor: '#fff',
        }}
      >
        <TextField
          fullWidth
          placeholder="Search users by username, name, email, phone, or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: stormGrey[400] }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: stormGrey[50],
            },
          }}
        />
      </Paper>

      {alertMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setAlertMessage('')}>
          {alertMessage}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {deleteError && <Alert severity="error" sx={{ mb: 1.5 }}>{deleteError}</Alert>}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${stormGrey[200]}`,
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(neonBlue[500], 0.06) }}>
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }}>Role</TableCell>
              {isSuperAdmin && (
                <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }}>Company</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700] }} align="center">
                Active
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: stormGrey[700], width: 100 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user, index) => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => handleRowClick(user)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: index % 2 === 1 ? alpha(stormGrey[100], 0.5) : '#fff',
                    '&:hover': { bgcolor: alpha(neonBlue[500], 0.06) },
                    '& td': { borderBottom: `1px solid ${stormGrey[200]}` },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{user.username}</TableCell>
                  <TableCell>{user.name || '-'}</TableCell>
                  <TableCell sx={{ color: stormGrey[600] }}>{user.email || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.phone || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      size="small"
                      sx={{ fontWeight: 500, ...getRoleChipSx(user.role) }}
                    />
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell sx={{ color: stormGrey[600] }}>{user.Company?.name || '-'}</TableCell>
                  )}
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={user.is_active}
                      onChange={(e) => handleStatusChange(e, user.id, e.target.checked)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Reset Password">
                      <IconButton
                        onClick={(e) => handleOpenResetPasswordModal(e, user)}
                        size="small"
                        sx={{
                          mr: 0.5,
                          color: shakespeare[500],
                          bgcolor: alpha(shakespeare[500], 0.08),
                          '&:hover': { bgcolor: alpha(shakespeare[500], 0.16) },
                        }}
                      >
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <IconButton
                        onClick={(e) => handleDeleteClick(e, user)}
                        size="small"
                        sx={{
                          color: redOrange[500],
                          bgcolor: alpha(redOrange[500], 0.08),
                          '&:hover': { bgcolor: alpha(redOrange[500], 0.16) },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {users.length > recordsPerPage && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
          />
        </Box>
      )}

      <ConfirmDialog
        open={openConfirmDialog}
        title="Confirm Delete"
        content={`Are you sure you want to delete user ${userToDelete?.username}?`}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setOpenConfirmDialog(false);
          setUserToDelete(null);
        }}
      />

      <EditUserModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        user={selectedUser}
        onUpdate={(updatedUser) => {
          setAllUsers(allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)));
          handleCloseEditModal();
        }}
        companies={companies}
        isSuperAdmin={isSuperAdmin}
        isCusAdmin={isCusAdmin}
        currentUserCompanyId={currentUserCompanyId}
      />

      <AddUserModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onUserCreated={handleUserCreated}
        companies={companies}
        isSuperAdmin={isSuperAdmin}
        isCusAdmin={isCusAdmin}
        currentUserCompanyId={currentUserCompanyId}
      />

      {userToResetPassword && (
        <ResetPasswordAdminModal
          open={isResetPasswordModalOpen}
          onClose={handleCloseResetPasswordModal}
          user={userToResetPassword}
          onPasswordReset={handlePasswordResetSuccess}
        />
      )}
    </Box>
  );
};

export default UserAdminPage;
